package com.evoapps.evoshift.widgets

import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.waitForUpOrCancellation
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.InterceptPlatformTextInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.PlatformTextInputInterceptor
import androidx.compose.ui.platform.PlatformTextInputMethodRequest
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.done
import org.jetbrains.compose.resources.stringResource

/*
 * SH-249: putting the keyboard away, once for the whole app instead of field by field.
 *
 * - iOS: the number and decimal pads have no return key, and a multi-line box's return adds a
 *   line, so nothing closed them. [ProvideKeyboardDismiss] sees every text input session that
 *   starts below it and lets the platform's [KeyboardDoneBar] (iOS only) dock a "Done" bar on
 *   such keyboards. Being part of the keyboard, the bar counts in its height, so iOS still lifts
 *   the focused field above both.
 * - Every platform: a tap on nothing (a background, a label: whatever did not take the tap) while
 *   a field is being edited puts the keyboard away ([dismissKeyboardOnTapOutside]).
 *
 * The app root provides it, and so does each dialog and sheet (a separate window with its own
 * focus): there Done and an outside tap move the focus back to the dialog, so its Esc and Enter
 * keep working.
 */

/**
 * The platform's keyboard accessory (iOS: a "Done" bar above the number pad). [decorate] returns
 * the [request] with the bar added when the keyboard needs one, or the request unchanged.
 * [onDone] puts the keyboard away.
 */
interface KeyboardDoneBar {
    fun decorate(request: PlatformTextInputMethodRequest, label: String, onDone: () -> Unit): PlatformTextInputMethodRequest
}

/** The platform's [KeyboardDoneBar]; null (none) except on iOS, which provides it at the root. */
val LocalKeyboardDoneBar = staticCompositionLocalOf<KeyboardDoneBar?> { null }

/** Whether a field is being edited in this window, and how to put its keyboard away. */
@Stable
class KeyboardDismissState internal constructor() {
    internal var sessions by mutableIntStateOf(0)
    internal var dismissAction: () -> Unit = {}

    /** A text field below has the keyboard (an input session is running). */
    val editing: Boolean get() = sessions > 0

    /** Puts the keyboard away (the focus leaves the field). */
    fun dismiss() = dismissAction()
}

/** The window's [KeyboardDismissState], from the nearest [ProvideKeyboardDismiss]. */
val LocalKeyboardDismiss = staticCompositionLocalOf<KeyboardDismissState?> { null }

/**
 * Watches the text fields in [content] (see the file comment). [onDismiss] puts the keyboard
 * away; by default the focus is cleared. A dialog passes "focus the dialog" instead.
 */
@Composable
fun ProvideKeyboardDismiss(onDismiss: (() -> Unit)? = null, content: @Composable () -> Unit) {
    val focusManager = LocalFocusManager.current
    val keyboard = LocalSoftwareKeyboardController.current
    val state = remember { KeyboardDismissState() }
    val latestDismiss by rememberUpdatedState(onDismiss)
    state.dismissAction = {
        latestDismiss?.invoke() ?: focusManager.clearFocus()
        keyboard?.hide()
    }
    val doneBar = LocalKeyboardDoneBar.current
    val latestDoneBar by rememberUpdatedState(doneBar)
    val label by rememberUpdatedState(stringResource(Res.string.done))
    // One interceptor for the composition's lifetime: a new instance restarts running sessions.
    val interceptor = remember(state) {
        PlatformTextInputInterceptor { request, next ->
            state.sessions++
            try {
                val decorated = latestDoneBar?.decorate(request, label) { state.dismiss() } ?: request
                next.startInputMethod(decorated)
            } finally {
                state.sessions--
            }
        }
    }
    InterceptPlatformTextInput(interceptor) {
        CompositionLocalProvider(LocalKeyboardDismiss provides state, content = content)
    }
}

/**
 * A tap that nothing below took (no button, list row, field or scroll used it) while a field is
 * being edited puts the keyboard away. It only watches the taps, in the last pass, so every
 * click below works as before.
 */
@Composable
fun Modifier.dismissKeyboardOnTapOutside(): Modifier {
    val state = LocalKeyboardDismiss.current ?: return this
    return pointerInput(state) {
        awaitEachGesture {
            val down = awaitFirstDown(requireUnconsumed = false, pass = PointerEventPass.Final)
            if (down.isConsumed || !state.editing) return@awaitEachGesture
            val up = waitForUpOrCancellation(PointerEventPass.Final) ?: return@awaitEachGesture
            if (!up.isConsumed && state.editing) state.dismiss()
        }
    }
}
